import { Link } from 'react-router-dom'
import { useAbruf, type Seite } from '../api'
import { Kennzahl, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, geld, zahl } from '../formate'

interface MakoUebersicht {
  offeneKlaerfaelle: number
  offeneFristen: number
  abgelaufeneFristen: number
}

interface Postenzeile {
  belegnummer: string
  kundeId: string
  faelligAm: string
  rueckstand: string
  mahnstufe: string
  art: string
}

interface Vertragszeile {
  vertragsnummer: string
}

/**
 * Die Startseite fasst zusammen, was am Tagesanfang zaehlt: was liegt an, was ist
 * ueberfaellig, was steht in der Marktkommunikation offen.
 */
export default function Uebersicht() {
  const mako = useAbruf<MakoUebersicht>('/api/mako/uebersicht')
  const vertraege = useAbruf<Seite<Vertragszeile>>(
    '/api/vertraege?status=IN_BELIEFERUNG&groesse=1',
  )
  const ueberfaellig = useAbruf<Postenzeile[]>('/api/debitoren/ueberfaellig')

  const rueckstand = (ueberfaellig.daten ?? []).reduce(
    (summe, posten) => summe + Number(posten.rueckstand),
    0,
  )

  return (
    <>
      <h1>Übersicht</h1>
      <p className="untertitel">
        Stand der Belieferung, der offenen Forderungen und der Marktkommunikation.
      </p>

      <div className="karten">
        <Kennzahl
          beschriftung="Verträge in Belieferung"
          wert={vertraege.daten ? zahl(vertraege.daten.gesamtzahl, 0) : '…'}
        />
        <Kennzahl
          beschriftung="Überfällige Posten"
          wert={ueberfaellig.daten ? zahl(ueberfaellig.daten.length, 0) : '…'}
          fussnote={ueberfaellig.daten ? `${geld(rueckstand)} Rückstand` : undefined}
        />
        <Kennzahl
          beschriftung="Offene Klärfälle"
          wert={mako.daten ? zahl(mako.daten.offeneKlaerfaelle, 0) : '…'}
        />
        <Kennzahl
          beschriftung="Abgelaufene Fristen"
          wert={mako.daten ? zahl(mako.daten.abgelaufeneFristen, 0) : '…'}
          fussnote={
            mako.daten && mako.daten.abgelaufeneFristen > 0
              ? 'Fristversäumnis prüfen'
              : undefined
          }
        />
      </div>

      <Karte
        titel="Überfällige Forderungen"
        handlung={<Link to="/debitoren">Zu den Debitoren</Link>}
      >
        <Zustand
          laeuft={ueberfaellig.laeuft}
          fehler={ueberfaellig.fehler}
          leer={(ueberfaellig.daten ?? []).length === 0}
          leertext="Keine überfälligen Posten. Nichts zu tun."
        >
          <Tabelle
            spalten={[
              { name: 'Belegnummer' },
              { name: 'Art' },
              { name: 'Fällig am' },
              { name: 'Mahnstufe' },
              { name: 'Rückstand', zahl: true },
            ]}
          >
            {(ueberfaellig.daten ?? []).slice(0, 12).map((posten) => (
              <tr key={posten.belegnummer}>
                <td>{posten.belegnummer}</td>
                <td>{posten.art}</td>
                <td>{datum(posten.faelligAm)}</td>
                <td>
                  <Merkmal art={posten.mahnstufe === 'KEINE' ? 'neutral' : 'warnung'}>
                    {posten.mahnstufe}
                  </Merkmal>
                </td>
                <td className="zahl">{geld(posten.rueckstand)}</td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}
